import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplet, ChevronLeft, AlertCircle, Plus, Trash2, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import type { CreateCompanyRequest, CreateCompanyResponse, CreateBranchRequest, CreateBranchResponse } from '../../../shared/contracts';

interface Branch {
  id: string;
  name: string;
  location: string;
}

interface FieldErrors {
  companyName?: string;
  companyEmail?: string;
}

export default function Company() {
  const navigate = useNavigate();
  const { updateUser, user } = useAuth();
  const [step, setStep] = useState<'company' | 'branches'>('company');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(false);

  // Company form state
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');

  // Branches state
  const [branches, setBranches] = useState<Branch[]>([
    { id: '1', name: '', location: '' },
  ]);

  // Fetch company data when user is in branch_setup status
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (user?.onboardingStatus === 'branch_setup') {
        setIsLoadingCompanyData(true);
        try {
          const response = await api.get<any>('/company');
          if (response.company) {
            setCompanyName(response.company.name);
            setCompanyEmail(response.company.email);
            setCompanyPhone(response.company.phone || '');
            setCompanyAddress(response.company.address || '');
          }
        } catch (err) {
          console.error('[Company] Failed to fetch company data:', err);
        } finally {
          setIsLoadingCompanyData(false);
        }
      }
    };

    fetchCompanyData();
  }, [user?.onboardingStatus]);

  // If user is already in branch_setup, skip company creation and show branches step
  useEffect(() => {
    if (user?.onboardingStatus === 'branch_setup') {
      setStep('branches');
      setIsEditingCompany(false);
    }
  }, [user?.onboardingStatus]);

  // Validation helpers
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleCreateCompany = async () => {
    const newFieldErrors: FieldErrors = {};

    // Validate company name
    if (!companyName.trim()) {
      newFieldErrors.companyName = 'Company name is required';
    }

    // Validate company email (required)
    if (!companyEmail.trim()) {
      newFieldErrors.companyEmail = 'Email is required';
    } else if (!isValidEmail(companyEmail)) {
      newFieldErrors.companyEmail = 'Please enter a valid email address';
    }

    // Show field errors if any
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      const request: CreateCompanyRequest = {
        name: companyName.trim(),
        email: companyEmail.trim(),
        phone: companyPhone.trim() || undefined,
        address: companyAddress.trim() || undefined,
      };

      const response = await api.post<CreateCompanyResponse>('/onboarding/company', request);

      if (response.success) {
        // Update user in context (status should be company_setup now)
        updateUser(response.user);
        setStep('branches');
      } else {
        setError('Failed to create company. Please try again.');
      }
    } catch (err) {
      console.error('[Company] Create company error:', err);
      setError('Failed to create company. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBranch = () => {
    setBranches([...branches, { id: Date.now().toString(), name: '', location: '' }]);
  };

  const handleSaveCompanyDetails = async () => {
    const newFieldErrors: FieldErrors = {};

    // Validate company name
    if (!companyName.trim()) {
      newFieldErrors.companyName = 'Company name is required';
    }

    // Validate company email (required)
    if (!companyEmail.trim()) {
      newFieldErrors.companyEmail = 'Email is required';
    } else if (!isValidEmail(companyEmail)) {
      newFieldErrors.companyEmail = 'Please enter a valid email address';
    }

    // Show field errors if any
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      const updateRequest = {
        name: companyName.trim(),
        email: companyEmail.trim(),
        phone: companyPhone.trim() || undefined,
        address: companyAddress.trim() || undefined,
      };

      const response = await api.put<any>('/company', updateRequest);

      if (response.success) {
        setIsEditingCompany(false);
      } else {
        setError('Failed to save company details. Please try again.');
      }
    } catch (err) {
      console.error('[Company] Save company error:', err);
      setError('Failed to save company details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBranch = (id: string) => {
    if (branches.length > 1) {
      setBranches(branches.filter((b) => b.id !== id));
    }
  };

  const handleBranchChange = (id: string, field: 'name' | 'location', value: string) => {
    setBranches(
      branches.map((b) => (b.id === id ? { ...b, [field]: value } : b))
    );
  };

  const handleCompleteBranchSetup = async () => {
    const validBranches = branches.filter((b) => b.name.trim());
    if (validBranches.length === 0) {
      setError('Please add at least one branch with a name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create the first branch to mark onboarding as complete
      const firstBranch = validBranches[0];
      const request: CreateBranchRequest = {
        name: firstBranch.name.trim(),
        location: firstBranch.location.trim() || undefined,
      };

      const response = await api.post<CreateBranchResponse>('/onboarding/branch', request);

      if (response.success) {
        // Update user in context (status should be completed now)
        updateUser(response.user);

        // Create additional branches if there are any
        if (validBranches.length > 1) {
          for (let i = 1; i < validBranches.length; i++) {
            const branch = validBranches[i];
            const branchRequest: CreateBranchRequest = {
              name: branch.name.trim(),
              location: branch.location.trim() || undefined,
            };
            await api.post('/branches', branchRequest);
          }
        }

        navigate('/dashboard');
      } else {
        setError('Failed to create branches. Please try again.');
      }
    } catch (err) {
      console.error('[Company] Create branch error:', err);
      setError('Failed to create branches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'company') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/welcome')}
              className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-6 font-medium"
            >
              <ChevronLeft className="h-5 w-5" />
              Back
            </button>

            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="bg-white rounded-full p-4 shadow-lg">
                  <Droplet className="h-12 w-12 text-cyan-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Create Company</h1>
              <p className="text-gray-600">Step 1 of 2: Company Details</p>
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-4 mb-6">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    setFieldErrors({ ...fieldErrors, companyName: undefined });
                  }}
                  disabled={loading}
                  placeholder="e.g., AquaSystems LLC"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors ${
                    fieldErrors.companyName
                      ? 'border-red-500 focus:ring-red-600 bg-red-50'
                      : 'border-gray-300 focus:ring-cyan-600'
                  }`}
                />
                {fieldErrors.companyName && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.companyName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={companyEmail}
                  onChange={(e) => {
                    setCompanyEmail(e.target.value);
                    setFieldErrors({ ...fieldErrors, companyEmail: undefined });
                  }}
                  disabled={loading}
                  placeholder="company@example.com"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors ${
                    fieldErrors.companyEmail
                      ? 'border-red-500 focus:ring-red-600 bg-red-50'
                      : 'border-gray-300 focus:ring-cyan-600'
                  }`}
                />
                {fieldErrors.companyEmail && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.companyEmail}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={companyPhone}
                  onChange={(e) => {
                    setCompanyPhone(e.target.value);
                    setError('');
                  }}
                  disabled={loading}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={companyAddress}
                  onChange={(e) => {
                    setCompanyAddress(e.target.value);
                    setError('');
                  }}
                  disabled={loading}
                  placeholder="123 Main St, City, State 12345"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <button
              onClick={handleCreateCompany}
              disabled={loading || !companyName.trim()}
              className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating...
                </>
              ) : (
                'Next: Add Branches'
              )}
            </button>

            <p className="text-gray-500 text-xs mt-4">
              You&apos;ll be the company owner. Other team members can join using invite codes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Branches step
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          {user?.onboardingStatus === 'branch_setup' && isEditingCompany ? (
            <button
              onClick={() => setIsEditingCompany(false)}
              className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-6 font-medium"
            >
              <ChevronLeft className="h-5 w-5" />
              Back to Branches
            </button>
          ) : user?.onboardingStatus === 'branch_setup' ? (
            <button
              onClick={() => setIsEditingCompany(true)}
              className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-6 font-medium"
            >
              Edit Company Details
            </button>
          ) : (
            <button
              onClick={() => setStep('company')}
              className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-6 font-medium"
            >
              <ChevronLeft className="h-5 w-5" />
              Back
            </button>
          )}

          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-4 shadow-lg">
                <Droplet className="h-12 w-12 text-cyan-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isEditingCompany ? 'Edit Company Details' : 'Add Service Areas'}
            </h1>
            <p className="text-gray-600">
              {isEditingCompany ? 'Update your company information' : 'Step 2 of 2: Create at least one branch'}
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {isEditingCompany ? (
            // Company Details View
            <>
              {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <div className="space-y-4 mb-6">
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      setFieldErrors({ ...fieldErrors, companyName: undefined });
                    }}
                    disabled={loading}
                    placeholder="e.g., AquaSystems LLC"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors ${
                      fieldErrors.companyName
                        ? 'border-red-500 focus:ring-red-600 bg-red-50'
                        : 'border-gray-300 focus:ring-cyan-600'
                    }`}
                  />
                  {fieldErrors.companyName && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.companyName}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={companyEmail}
                    onChange={(e) => {
                      setCompanyEmail(e.target.value);
                      setFieldErrors({ ...fieldErrors, companyEmail: undefined });
                    }}
                    disabled={loading}
                    placeholder="company@example.com"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors ${
                      fieldErrors.companyEmail
                        ? 'border-red-500 focus:ring-red-600 bg-red-50'
                        : 'border-gray-300 focus:ring-cyan-600'
                    }`}
                  />
                  {fieldErrors.companyEmail && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.companyEmail}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={companyPhone}
                    onChange={(e) => {
                      setCompanyPhone(e.target.value);
                      setError('');
                    }}
                    disabled={loading}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={companyAddress}
                    onChange={(e) => {
                      setCompanyAddress(e.target.value);
                      setError('');
                    }}
                    disabled={loading}
                    placeholder="123 Main St, City, State 12345"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveCompanyDetails}
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Done Editing'
                )}
              </button>
            </>
          ) : (
            // Branches View
            <>
              {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

          <div className="space-y-4 mb-6">
            {branches.map((branch, index) => (
              <div key={branch.id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">Branch {index + 1}</h3>
                  {branches.length > 1 && (
                    <button
                      onClick={() => handleRemoveBranch(branch.id)}
                      disabled={loading}
                      className="text-red-600 hover:text-red-700 p-1 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {/* Branch Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branch Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={branch.name}
                      onChange={(e) => handleBranchChange(branch.id, 'name', e.target.value)}
                      disabled={loading}
                      placeholder="e.g., North District"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={branch.location}
                      onChange={(e) => handleBranchChange(branch.id, 'location', e.target.value)}
                      disabled={loading}
                      placeholder="e.g., Downtown area"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-600 focus:border-transparent outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleAddBranch}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-cyan-300 text-cyan-600 hover:bg-cyan-50 disabled:opacity-50 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors mb-6"
          >
            <Plus className="h-5 w-5" />
            Add Another Branch
          </button>

          <button
            onClick={handleCompleteBranchSetup}
            disabled={loading || !branches.some((b) => b.name.trim())}
            className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mb-4"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Creating...
              </>
            ) : (
              'Complete Setup'
            )}
          </button>

          <p className="text-gray-500 text-xs text-center">
            You can add more branches later from your dashboard
          </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
